import { getBookingPrecision } from '@clubmed/domains-service';
import {
  activityCategoryFragment,
  activityFragment,
  thematicFragment,
  rentalsFragment,
} from 'Client/cms/toolkit/product-utils/productFeatures.queries';
import {
  getStayAccommodationFragment,
  packagesFragment,
  priceDetailsFragment,
} from 'Views/account/queries/account.queries';
import {
  childcaresQuery,
  insuranceQuery,
  servicesWithSchedulePricingInfoQuery,
  transfersQuery,
  whiteLabelServicesQuery,
} from 'Components/utils/booking/booking-additional-services/BookingAddtionalServices.utils';
const CHILDREN_ACTIVITY_IDENTIFIER = '_CHILD_';

const getStayBookingFragment = (appContext) => {
  const precision = getBookingPrecision(appContext.locale);
  return `
  fragment booking on CustomerBookingWithoutStaysType {
    attendees {
      birthday
      customerId
      firstName:firstname
      id
      lastName:lastname
      mainContact
      unicodeFirstname
      unicodeLastname
    }
    categories {
      isOption
      isOptionRenewed
      isExpiredOption
      isFromRenewal
      isStandard
      isTravelPlan
      isTravelPlanPrebooking
      isTravelPlanStandard
    }
    creationDate
    dates {
      duration
      endDate
      startDate
    }
    id
    isMultiSaleContracts
    isMultiStays
    membership {
      expirationDate
      isExpired
      points
      type
    }
    optionDurability {
      expirationDateTime
      isReliable
    }
    packages {
      attendeeIds
      packageId
      upsellPrice {
        currency
        formattedValue(precision: ${precision})
        value
        }
    }
    payment {
      due {
        deadlineDate
        formattedValue
        schedules {
          deadlineDate
          formattedValue
          value
        }
        value
      }
      isEnabled
      paid {
        formattedValue
        value
      }
      status {
        isInCredit
        isPaid
        isPending
        isUnPaid
      }
      total {
        formattedValue
        value
      }
    }
    status {
      name
      isArchived
      isCanceled
      isExpiredOption
      isOption
      isPendingPayment
      isPartiallyValidated
      isSearch
      isValidated
    }
    totalPrice {
      formattedValue
      value
      details {
        cancellationInsurance {
          formattedValue(precision: ${precision})
          value
        }
        discount {
          formattedValue(precision: ${precision})
          value
        }
        donation {
          formattedValue(precision: ${precision})
          value
        }
        fees {
          formattedValue(precision: ${precision})
          value
        }
        onDemand {
          formattedValue(precision: ${precision})
          id
          label
          value
        }
        package {
          formattedValue(precision: ${precision})
          value
        }
        total {
          formattedValue(precision: ${precision})
          value
        }
      }
    }
    vendor {
      isSoldByClubmed
      isSoldByPartners
      name
      phoneNumber
      type
    }
  }`;
};

const getStayProductFragment = () => `
  fragment product on Resort {
    associatedResort {
      id
      productTypeId
    }
    activities {
      description
      id
      included
      logo
      name
    }
    activityThematics {
      id
      title
      description
      images
      activityCount {
        included
        onDemand
      }
      activities {
        ageInMonths {
          max
          min
        }
        description
        durationDescription
        id
        isIncluded
        label
        practiceLevelDescription
      }
    }
    activityCategories {
      leisure {
        id
        label
        subCategories {
          activities {
            ageInMonths {
              max
              min
            }
            description
            durationDescription
            id
            isIncluded
            label
            practiceLevelDescription
          }
          id
          imageUrl
          keywords
          label
        }
      }
      wellness {
        id
        label
        subCategories {
          activities {
            ageInMonths {
              max
              min
            }
            description
            durationDescription
            id
            isIncluded
            label
            practiceLevelDescription
          }
          id
          imageUrl
          keywords
          label
        }
      }
    }
    thematicActivityTree {
      ...thematics
      activities {
        ...activity
      }
    }
    activityTree {
      ...activityCategory
      activities {
        ...activity
      }
      categories {
        ...activityCategory
        activities {
          ...activity
        }
        categories {
          ...activityCategory
          activities {
            ...activity
          }
        }
      }
    }
    arrivalCity
    badgeIcon
    category
    childcareDetails {
      description
      id
      included
      logo
      name
    }
    childrenActivities: productActivities(idFilter: "${CHILDREN_ACTIVITY_IDENTIFIER}") {
      ageInMonths {
        max
        min
      }
      description
      equipments {
        id
        label
      }
      id
      isIncluded
      label
      subtitle
    }
    color
    comfort
    comfortLabel
    coordinates {
      latitude
      longitude
    }
    departureCity
    destination {
      country
    }
    duration
    esfResortId
    facilities(types: ["CHILDREN"], withEquipments: true) {
      equipments {
        id
        label
      }
      label
      subtitle
    }
    factSheetUrl
    hasECSpace
    hasMobileApp
    href
    icon
    id
    image
    images
    isCruise
    isExclusiveCollection
    isExplicitEC
    isJoyview
    isSummer
    isTour
    name
    practicalInformation {
      address {
        additionalInformation1
        additionalInformation2
        addOn
        city
        country
        stateOrDistrict
        streetName
        streetNumber
        town
        zipCode
      }
      checkInformations {
        furtherInformation
      }
      excludedInPackage
      healthFormalities
      includedInPackage
      passportAndVisas
      phone
    }
    price {
      termsAndConditions {
        departureCity {
          name
        }
      }
    }
    rentalsTree {
      ...rentals
    }
    seasonName
    transfers {
      access {
        id
        label
        type
      }
      duration
      id
      label
      means {
        id
        label
      }
    }
    travelSherpaInbound
    travelSherpaOutbound
    type
  }`;

export const getStayTransportFragment = () => `
  fragment transportsDetails on TransportDetailType {
    durationInMin
    stayId
    transferIncluded
    travelSections {
      attendees {
        id
        pnr
        reservation_number
        status
        ticket_number
      }
      arrival {
        airportLabel
        cityId
        date
        id
        label
        terminalCode
        time
      }
      connectionDurationInMin
      departure {
        airportLabel
        cityId
        date
        id
        label
        terminalCode
        time
      }
      durationInMin
      isClubmedTransport
      stopDurationInMin
      technicalStops
      transport {
        baggage {
          quantity
          weight
          unit
        }
        class
        companyExploiter {
          id
          label
          logo
        }
        companyOperator {
          id
          label
          logo
          url
        }
        reference
        travelNumber
        type
        carbonEmission
        averageCarbonEmission
      }
    }
  }`;
export const getCustomerBookingQuery = (
  bookingId: string,
  stayId: string,
  appContext: AppContext,
) => {
  return `query {
    customerBooking(id: "${bookingId}") {
      id
      stays(ids: ["${stayId}"]) {
        accommodations {
          ...accommodations
        }
        accommodationsCount
        booking {
          ...booking
        }
        dates {
          duration
          endDate
          isCurrent
          isPast
          isToCome
          startDate
        }
        features {
          easyArrival {
            status {
              isAvailable
              isDone
            }
            url
          }
          easyCheckInOut {
            easyCheckIn {
              isActivated
              status {
                isAvailable
                isUneditable
              }
            }
            easyCheckOut {
              isActivated
              status {
                isAvailable
                isUneditable
              }
            }
          }
          easyTransfer {
            serviceStatus {
              isValidated
              isUpsellAvailable
            }
          }
          feedback {
            status {
              isAvailable
            }
            url
          }
        }
        id
        participants {
          adultsCount
          attendees {
            birthday
            firstname
            id
            isAdult
            isKid
            lastname
            unicodeFirstname
            unicodeLastname
          }
          childrenCount
          totalCount
        }
        product {
          ...product
        }
        staff {
          categories {
            code
            label
          }
          codes
          description
          firstName
          job
          label
          picture
        }
        stayIndex
        transfers {
          id
          inBound {
            from {
              id
              label
            }
            to {
              id
              label
            }
            startDate
            endDate
          }
          outBound {
            from {
              id
              label
            }
            to {
              id
              label
            }
            startDate
            endDate
          }
          status
        }
        transport {
          fromCity
          fromCityCode
          inbounds {
            ...transportsDetails
          }
          outbounds {
            ...transportsDetails
          }
          toCity
          toCityCode
          type
          types
        }
        upsells(types: [CHILDCARE, EXCURSION, INSURANCE, LAND_SPORT, RENTAL, TRANSFER, WATER_SPORT, WINTER_SPORT, WELLNESS, PARKING, CONCIERGERIE]) {
          activities {
            ${servicesWithSchedulePricingInfoQuery}
            productInfo {
              description
              id
              isIncluded
              label
              parentActivityCategoriesId
            }
          }
          rentals {
            ${servicesWithSchedulePricingInfoQuery}
            productInfo {
              id
              label
              ageInMonths {
                min
                max
              }
              useCase {
                id
                label
              }
              shortDescription
              typeId: type
              typeLabel
              description: longDescription
              sizes {
                min
                max
                unit
              }
              practiceLevels {
                id
                label
              }
            }
          }
          ${childcaresQuery}
          ${insuranceQuery(appContext)}
          ${packagesFragment}
          ${transfersQuery(appContext)}
          ${whiteLabelServicesQuery(appContext)}
        }
      }
    }
  }
  ${getStayBookingFragment(appContext)}
  ${getStayProductFragment()}
  ${getStayAccommodationFragment()}
  ${getStayTransportFragment()}
  ${priceDetailsFragment(appContext)}
  ${activityCategoryFragment}
  ${activityFragment}
  ${thematicFragment}
  ${rentalsFragment}`;
};
export const getBookingUpgradeRoomQuery: (arg0: {
  productId: string;
  bookingId: string;
  customerId: string;
  attendeeIds: Array<string>;
  currentCategoryId: string;
  associatedResortProductTypeId: string;
  associatedResortId: string;
  nightsNumber: number;
}) => string = ({
  productId,
  bookingId,
  customerId,
  attendeeIds,
  currentCategoryId,
  associatedResortProductTypeId,
  associatedResortId,
  nightsNumber,
}) => `query {
  upgradeRoom (productId:"${productId}" bookingId:"${bookingId}" customerId:"${customerId}" attendeeIds: ["${attendeeIds.join('","')}"] currentCategoryId: "${currentCategoryId}" associatedResortProductTypeId: "${associatedResortProductTypeId}" associatedResortId:"${associatedResortId}" nightsNumber: ${nightsNumber}) {
    preview {
      imageUrls
      lowestPrice
      lowestPriceByNight
      isUpgradableOnline
      upgradeAvailable
      isVillaOnly
    }
    search {
      area {
        max
        min
        unit
      }
      bathroomEquipments {
        id
        label
        ranking
      }
      category
      categoryId
      comfortId
      comfortType
      description
      differentialPrice {
        amount
        currency
      }
      differentialPriceByNight {
        amount
        currency
      }
      isUpgradableOnline
      equipments {
        id
        label
        ranking
      }
      exclusiveCollection
      floorPlans
      hasArrangement
      hasDistribution
      hasSharedRoom
      id
      isVilla
      label
      maxCapacity
      media {
        photos {
          url
        }
      }
      minCapacity
      outsideFacilities {
        id
        label
      }
      ranking
      roomType
      selection {
        attendees {
          birthdate
          id
          type
        }
        id
        occupancy
        quantity
      }
      services {
        id
        label
        ranking
      }
      stock
      strengths {
        id
        label
      }
      surface
      view {
        id
        label
      }
    }
  }
}`;
export const getStreetViewsQuery = (productId: string, accommodationCategoryIds: Array<string>) => {
  const formattedAccommodationCategoryIds = accommodationCategoryIds
    .map((id) => `"${id}"`)
    .join(',');
  return `query {
    streetViews(productId: "${productId}", accommodationCategoryIds:[${formattedAccommodationCategoryIds}]) {
      accommodationCategoryId
      streetView {
        latitude
        longitude
        heading
        pitch
        fov
        url
      }
    }
  }`;
};
export const selectAccommodation: (arg0: {
  customerId: string;
  bookingId: string;
  selection: AccommodationSelection;
  amount: number;
  currency: string;
}) => string = ({ customerId, bookingId, selection, amount, currency }) => {
  return `mutation {
    upgradeRoomSelection(bookingId: "${bookingId}", customerId: "${customerId}", id: "${selection?.id}", attendees: ${JSON.stringify(selection?.attendees || '').replace(/"([^"]+)":/g, '$1:')}, quantity: ${selection?.occupancy}, occupancy: ${selection?.occupancy}, amount: ${amount}, currency: "${currency}"
    ) {
      data
    }
  }`;
};
